/*
 * DSD Overlay Manager – Phase 1
 *
 * Provides a simple overlay stack to manage modals and panels in the
 * canonical social core.  The manager maintains an array of active
 * overlays, toggles body scroll locking and emits events for lifecycle
 * hooks.  Future phases may extend this manager with z‑index tiers,
 * focus trapping and safe‑area adjustments.
 */
class OverlayManager {
  constructor() {
    this.stack = [];
    this.root = document.querySelector('.dsd-overlay-root') || null;
    if (this.root) {
      this.root.setAttribute('data-dsd-owner', 'OverlayManager');
      this.root.setAttribute('data-dsd-overlay-manager-bound', 'true');
    }
  }

  /**
   * Ensure the root overlay container exists and is appended to the body.
   */
  _ensureRoot() {
    const existingRoot = document.querySelector('.dsd-overlay-root');
    if (existingRoot) {
      this.root = existingRoot;
      this.root.setAttribute('data-dsd-owner', 'OverlayManager');
      this.root.setAttribute('data-dsd-overlay-manager-bound', 'true');
      return;
    }
    if (!this.root) {
      this.root = document.createElement('div');
      this.root.className = 'dsd-overlay-root';
      this.root.setAttribute('data-dsd-owner', 'OverlayManager');
      this.root.setAttribute('data-dsd-overlay-manager-bound', 'true');
      document.body.appendChild(this.root);
    }
  }

  /**
   * Open a new overlay with the given id and content.  Content should be a
   * DOM element.  Options may include callbacks for lifecycle hooks.
   *
   * @param {string} id Unique overlay identifier.
   * @param {HTMLElement} content The overlay’s content element.
   * @param {object} [options] Optional configuration.
   */
  open(id, content, options = {}) {
    this._ensureRoot();
    // If an overlay with the same id exists, close it first to avoid
    // duplicates.
    this.close(id);
    const overlayWrapper = document.createElement('div');
    overlayWrapper.className = 'dsd-overlay';
    overlayWrapper.dataset.overlayId = id;
    overlayWrapper.setAttribute('data-dsd-overlay-state', 'open');
    overlayWrapper.appendChild(content);
    this.root.appendChild(overlayWrapper);
    this.root.setAttribute('aria-hidden', 'false');
    this.stack.push({ id, wrapper: overlayWrapper, options });
    document.body.classList.add('dsd-overlay-open');
    // Notify listeners that an overlay has opened.  If the caller
    // provided an onOpen hook, call it now.
    if (options.onOpen) options.onOpen();
    // Record overlay opening in the runtime registry.  Use the
    // DSDOverlayManager as the owner for consistency across all
    // overlays.  Include the current stack length in the metadata.
    if (window.DSDRegistry && typeof window.DSDRegistry.openOverlay === 'function') {
      try {
        window.DSDRegistry.openOverlay(id, 'OverlayManager', {
          stackLength: this.stack.length
        });
      } catch (ex) {
        /* ignore registry errors */
      }
    }
  }

  /**
   * Close the overlay with the given id.  If no id is provided, close the
   * topmost overlay.
   *
   * @param {string} [id] Identifier of the overlay to close.
   */
  close(id) {
    if (!this.root) return;
    let entry;
    if (id) {
      const index = this.stack.findIndex((o) => o.id === id);
      if (index === -1) return;
      entry = this.stack.splice(index, 1)[0];
    } else {
      entry = this.stack.pop();
    }
    if (entry) {
      if (entry.options.onClose) entry.options.onClose();
      this.root.removeChild(entry.wrapper);
      if (this.stack.length === 0) {
        document.body.classList.remove('dsd-overlay-open');
        this.root.setAttribute('aria-hidden', 'true');
      }
      // Notify registry that the overlay has closed.  Use the ID
      // stored in the entry.  Do not attempt to close if registry is
      // unavailable.
      if (window.DSDRegistry && typeof window.DSDRegistry.closeOverlay === 'function') {
        try {
          window.DSDRegistry.closeOverlay(entry.id);
        } catch (ex) {
          /* ignore registry errors */
        }
      }
    }
  }
}

// Expose a global instance and register with the debugger.
window.DSDOverlayManager = new OverlayManager();
if (window.DSD_DEBUGGER && window.DSD_DEBUGGER.registerSubsystem) {
  window.DSD_DEBUGGER.registerSubsystem('OverlayManager', 'active');
}
